import React, { useEffect, useState } from "react";
import { Col, Form, Row, Spinner } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Swal from "sweetalert2";
import { authHeader } from "../../../services/auth-header";
import { BASE_URL } from "../../../services/Api";
import debounce from 'lodash/debounce';
import CommonManufacturingSalesInternationalFields from "./CommonManufacturingSalesInternationalFields";

const SalesAMCreate = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    customer: null,
    customerDetails: {
      contactperson: '',
      contactno: '',
      email: '',
      paymentterm: '',
      incoterms: ''
    },
    customerMultipleAddress: null,
    shipTo: null,
    billTo: null,
    branch: null,
    warehouse: null,
    tallyreferenceno: '',
    dispatchthrough: '',
    destination: '',
    reference: '',
    packinglistno: '',
    packinglistdate: '',
    buyerorderno: '',
    buyerorderdate: '',

    // New international fields
    kindattn: '',
    countryofdestination: '',
    countryoforigin: '',
    airsea: '',
    portofloading: '',
    portofdischarge: '',
    noofboxes: '',
    netweightofboxes: '',
    grossweightofboxes: '',
    lutbondno: '',
    typeofshippingbill: '',
    drawbackacno: '',
    chapterheading: '',
    tariffitem: '',
    description: '',
    drawbackrate: '',
    lut: '',
    lutbonddate: '',
    fromdate: '',
    todate: '',

    rows: [],
    exchangeRate: 1,
    exportchargescurrency: 0,
    exportpreperationcharges: '',
    selectedCurrency: " "

  });

  const [invoicedate, setinvoicedate] = useState('');
  const [invoiceno, setinvoiceno] = useState('');
  const [enquiryValidationMsg, setEnquiryValidationMsg] = useState('');
  const [isEnquiryValid, setIsEnquiryValid] = useState(false);
  const [formSubmitted, setFormSubmitted] = useState(false);

  const checkinvoiceno = async (number) => {
    if (!number) {
      setEnquiryValidationMsg('');
      setIsEnquiryValid(false);
      return;
    }

    if (!/^[A-Za-z0-9-]+$/.test(number)) {
      setEnquiryValidationMsg('❌ Only alphanumeric characters and hyphens are allowed');
      setIsEnquiryValid(false);
      return;
    }

    try {
      const response = await axios.get(`${BASE_URL}sales/check/${number}`, {
        headers: authHeader(),
      });

      if (response.data === true) {
        setEnquiryValidationMsg('❌ This sales number already exists.');
        setIsEnquiryValid(false);
      } else if (response.data === false) {
        setEnquiryValidationMsg('✅ This sales number is available.');
        setIsEnquiryValid(true);
      } else {
        setEnquiryValidationMsg('Unexpected response from server.');
        setIsEnquiryValid(false);
      }
    } catch (error) {
      console.error("Error checking enquiry number:", error);
      setEnquiryValidationMsg('❌ Error validating sales number.');
      setIsEnquiryValid(false);
    }
  };

  useEffect(() => {
    const debouncedCheck = debounce(() => {
      checkinvoiceno(invoiceno);
    }, 400);

    debouncedCheck();

    return () => debouncedCheck.cancel();
  }, [invoiceno]);

  // Handle data changes from child component
  const handleDataChange = (newData) => {
    setFormData(prev => ({
      ...prev,
      ...newData,
      rows: newData.rows || prev.rows
    }));
  };

  // Submit new sales Order
  const handleSubmit = async () => {
    setIsLoading(true);
    setFormSubmitted(true);
    if (!isEnquiryValid) {
      Swal.fire({
        icon: 'error',
        title: 'Validation Error',
        text: 'Please enter a valid sales number before submitting.',
      });
      return;
    }

    const {
      customer,
      customerDetails,
      customerMultipleAddress,
      shipTo,
      billTo,
      branch,
      warehouse,

      tallyreferenceno,
      dispatchthrough,
      destination,
      reference,
      packinglistno,
      packinglistdate,
      buyerorderno,
      buyerorderdate,

      // New international fields
      kindattn,
      countryofdestination,
      countryoforigin,
      airsea,
      portofloading,
      portofdischarge,
      noofboxes,
      netweightofboxes,
      grossweightofboxes,
      lutbondno,
      typeofshippingbill,
      drawbackacno,
      chapterheading,
      tariffitem,
      description,
      drawbackrate,
      lut,
      lutbonddate,
      fromdate,
      todate,

      rows,
      exchangeRate,
      exportchargescurrency,
      exportpreperationcharges,
      selectedCurrency

    } = formData;

    // Validation
    if (!customer) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Please select a customer',
      });
      setIsLoading(false);
      return;
    }

    if (rows.length === 0) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Please add at least one product',
      });
      setIsLoading(false);
      return;
    }

    // Calculate totals
    const misccharges = exportchargescurrency * exchangeRate;
    const netTotalInr = rows.reduce((total, row) => total + (row.totalInr || 0), 0);
    const netTotalCurrency = rows.reduce((total, row) => total + (row.totalcurrency || 0), 0);
    const grandTotalInr = netTotalInr + misccharges;
    const grandTotalCurrency = netTotalCurrency + exportchargescurrency;

    // Prepare sales order items
    const salesItems = rows.map(row => ({
      product: { id: row.value },
      productremarks: row.productremarks || '',
      quantity: row.quantity || 0,
      unitrateinr: row.unitRateInr || 0,
      totalinr: row.totalInr || 0,
      unitratecurrency: row.unitratecurrency || 0,
      totalcurrency: row.totalcurrency || 0,
      exchangerate: row.exchangerate || exchangeRate,
      itemcode: row.itemcode || '',
      hsnsacno: row.hsnsacno || '',
      serialnumber: row.serialnumber || '',
      modelnumber: row.modelnumber || '',
      tagnumber: row.tagnumber || '',
      moc: row.moc || '',
      partno: row.partno || '',
      uom: row.uom || '',
      currency: selectedCurrency,
    }));

    // Prepare main Sales Order data with all international fields
    const SalesData = {
      salestype: "Manufacturing",
      salescategory: "International",
      tallyreferenceno,
      dispatchthrough,
      destination,
      reference,
      packinglistno,
      packinglistdate,
      buyerorderno,
      buyerorderdate,
      invoiceno,
      invoicedate,

      // New international fields
      kindattn,
      countryofdestination,
      countryoforigin,
      airsea,
      portofloading,
      portofdischarge,
      noofboxes,
      netweightofboxes,
      grossweightofboxes,
      lutbondno,
      typeofshippingbill,
      drawbackacno,
      chapterheading,
      tariffitem,
      description,
      drawbackrate,
      lut,
      lutbonddate,
      fromdate,
      todate,

      salesItems,
      customer: { id: customer.value },
      customeraddress: customerMultipleAddress ? { id: customerMultipleAddress.value } : null,
      shipto: shipTo ? { id: shipTo?.value } : null,
      billto: billTo ? { id: billTo?.value } : null,
      contactperson: customerDetails.contactperson,
      contactno: customerDetails.contactno,
      email: customerDetails.email,
      paymentterm: customerDetails.paymentterm,
      incoterms: customerDetails.incoterms,
      branch: branch ? { id: branch.value } : null,
      warehouse: warehouse ? { id: warehouse.value } : null,
      salescurrency: selectedCurrency,
      salesexchangerate: exchangeRate,
      grandtotalinr: grandTotalInr,
      grandtotalcurrency: grandTotalCurrency,
      exportpreperationchargescurrency: exportchargescurrency,
      exportpreperationcharges,
      nettotalinr: netTotalInr,
      nettotalcurrency: netTotalCurrency,
    };

    try {
      await axios.post(`${BASE_URL}sales/save`, SalesData, {
        headers: authHeader()
      });

      Swal.fire({
        icon: 'success',
        title: 'Success',
        text: 'Sales created successfully!',
      });
      navigate("/salesindex/1");
    } catch (error) {
      console.error("Error creating Sales:", error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.response?.data?.message || 'Failed to create Sales. Please try again.',
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="container">
      <Form>
        <Row>
          <Col md={6}>
            <Form.Group>
              <Form.Label>Sales Invoice No.: *</Form.Label>
              <Form.Control
                type="text"
                value={invoiceno}
                onChange={e => {
                  setinvoiceno(e.target.value);
                  setFormSubmitted(false);
                }}
                isInvalid={
                  (formSubmitted && !isEnquiryValid) ||
                  enquiryValidationMsg.includes('exists') ||
                  enquiryValidationMsg.includes('Error') ||
                  enquiryValidationMsg.includes('allowed')
                }
                required
              />
              {enquiryValidationMsg && (
                <Form.Control.Feedback type={
                  enquiryValidationMsg.includes('exists') ||
                    enquiryValidationMsg.includes('Error') ||
                    enquiryValidationMsg.includes('allowed') ?
                    "invalid" : "valid"
                }>
                  {enquiryValidationMsg}
                </Form.Control.Feedback>
              )}
              {formSubmitted && !isEnquiryValid && !enquiryValidationMsg && (
                <Form.Control.Feedback type="invalid">
                  Please enter a valid enquiry number
                </Form.Control.Feedback>
              )}
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Sales Invoice Date</Form.Label>
              <Form.Control
                type="date"
                value={invoicedate}
                onChange={(e) => setinvoicedate(e.target.value)}
              />
            </Form.Group>
          </Col>
        </Row>

        <CommonManufacturingSalesInternationalFields
          onDataChange={handleDataChange}
          initialData={formData}
        />

        <br />
        <div className="d-flex justify-content-end">
          <button
            className="btn btn-primary me-2"
            type="button"
            onClick={handleSubmit}
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <Spinner as="span" animation="border" size="sm" />
                Creating...
              </>
            ) : 'CREATE SALES'}
          </button>

          <button
            className="btn btn-secondary"
            type="button"
            onClick={() => navigate("/pindex/1")}
            disabled={isLoading}
          >
            CANCEL
          </button>
        </div>
      </Form>
    </div>
  );
};

export default SalesAMCreate;
